import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import DataTable, { Column } from '../../components/common/DataTable';
import StatusChip from '../../components/common/StatusChip';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { getVictimReports, getVictimStats } from '../../services/api/reportApi';
import { VictimProfile } from '../../types/report';
import { formatDateTime, formatCurrency } from '../../utils/formatters';

const victimTypeLabels: Record<string, string> = {
  voice_phishing: '보이스피싱',
  smishing: '스미싱',
  pharming: '파밍',
  other: '기타',
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const VictimReportListPage: React.FC = () => {
  const [filters, setFilters] = useState<{
    victimType?: string;
    page: number;
    limit: number;
  }>({
    page: 0,
    limit: 20,
  });
  const [selectedReport, setSelectedReport] = useState<VictimProfile | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['victimReports', filters],
    queryFn: () => getVictimReports(filters),
  });

  const { data: stats } = useQuery({
    queryKey: ['victimStats'],
    queryFn: getVictimStats,
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  const handleVictimTypeFilterChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      victimType: value === 'all' ? undefined : value,
      page: 0,
    }));
  };

  const handleRowClick = (report: VictimProfile) => {
    setSelectedReport(report);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedReport(null);
  };

  const columns: Column<VictimProfile>[] = [
    {
      id: 'victimType',
      label: '피해 유형',
      minWidth: 120,
      format: (value: string) => victimTypeLabels[value] || value,
    },
    {
      id: 'user',
      label: '신고자',
      minWidth: 150,
      format: (_value: any, row: VictimProfile) => row.user.username,
    },
    {
      id: 'incidentDate',
      label: '피해 날짜',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
      sortable: true,
    },
    {
      id: 'amount',
      label: '피해 금액',
      minWidth: 120,
      align: 'right',
      format: (value: number) => formatCurrency(value),
    },
    {
      id: 'isPublic',
      label: '공개 여부',
      minWidth: 100,
      align: 'center',
      format: (value: boolean) => (
        <StatusChip status={value ? 'active' : 'inactive'} label={value ? '공개' : '비공개'} />
      ),
    },
    {
      id: 'createdAt',
      label: '신고일',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
      sortable: true,
    },
  ];

  // 유형별 통계 데이터 준비
  const pieChartData = stats
    ? Object.entries(stats.byType).map(([type, count]) => ({
        name: victimTypeLabels[type] || type,
        value: count,
      }))
    : [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        피해 신고 관리
      </Typography>

      {/* 통계 카드 */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              총 피해 건수
            </Typography>
            <Typography variant="h4">{stats?.totalCases || 0}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              총 피해 금액
            </Typography>
            <Typography variant="h4">{formatCurrency(stats?.totalAmount || 0)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              보이스피싱
            </Typography>
            <Typography variant="h4">{stats?.byType.voice_phishing || 0}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              스미싱
            </Typography>
            <Typography variant="h4">{stats?.byType.smishing || 0}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* 유형별 분포 차트 */}
      {pieChartData.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            피해 유형별 분포
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* 필터 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>피해 유형 필터</InputLabel>
            <Select
              value={filters.victimType || 'all'}
              label="피해 유형 필터"
              onChange={e => handleVictimTypeFilterChange(e.target.value)}
              size="small"
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="voice_phishing">보이스피싱</MenuItem>
              <MenuItem value="smishing">스미싱</MenuItem>
              <MenuItem value="pharming">파밍</MenuItem>
              <MenuItem value="other">기타</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* 데이터 테이블 */}
      <DataTable
        columns={columns}
        data={data?.items || []}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onRowClick={handleRowClick}
        loading={isLoading}
      />

      {/* 상세 정보 모달 */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
        <DialogTitle>피해 신고 상세 정보</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ pt: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      피해 유형
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {victimTypeLabels[selectedReport.victimType] || selectedReport.victimType}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      신고자
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedReport.user.username} ({selectedReport.user.email})
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      피해 날짜
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDateTime(selectedReport.incidentDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      피해 금액
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatCurrency(selectedReport.amount)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      공개 여부
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <StatusChip
                        status={selectedReport.isPublic ? 'active' : 'inactive'}
                        label={selectedReport.isPublic ? '공개' : '비공개'}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      신고일
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDateTime(selectedReport.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                {selectedReport.scammerPhones && selectedReport.scammerPhones.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      사기범 전화번호
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedReport.scammerPhones.join(', ')}
                    </Typography>
                  </Box>
                )}
                {selectedReport.scammerAccounts && selectedReport.scammerAccounts.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      사기범 계좌번호
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedReport.scammerAccounts.join(', ')}
                    </Typography>
                  </Box>
                )}
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    피해 내용
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                    {selectedReport.description || '내용 없음'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VictimReportListPage;
